import { inject, Injectable } from '@angular/core';
import { FilterSchedulerApiService } from '../api/scheduler.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { FilterSchedulerMachine } from '@dep/filters/models';
import { FilterSchedulerItem } from '../../models/item';
import { SchedulerContainerService } from '../../../../common/scheduler/services/container.service';
import { RoutingService } from '../../../../core/services/routing.service';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { ResponsePlanningDayQuantity } from 'src/app/api/elite/dto/daily-planning.dto';
import { AssemblyType } from '../../../../api/elite/types/assembly-type';
import { FilterSchedulerMachineStage } from '@dep/filters/view-scheduler/types';

@Injectable()
export class FilterSchedulerViewService {
  private readonly schedulerApiService = inject(FilterSchedulerApiService);
  private readonly containerService = inject(SchedulerContainerService);
  private readonly routingService = inject(RoutingService);

  private machines = new BehaviorSubject<FilterSchedulerMachine[]>([]);
  machines$ = this.machines.asObservable();

  private items = new BehaviorSubject<FilterSchedulerItem[]>([]);
  items$ = this.items.asObservable();

  private typeOfItems = new BehaviorSubject<
    (AssemblyType & { selected: boolean })[]
  >([]);
  typeOfItems$ = this.typeOfItems.asObservable();

  form = new FormGroup({
    search: new FormControl<string>(undefined),
    searchList: new FormControl<string>(undefined),
  });

  searchList$ = this.form.controls.searchList.valueChanges.pipe(
    debounceTime(300)
  );

  filterItems$ = combineLatest([this.items$, this.typeOfItems$]).pipe(
    map(([items, types]) => {
      const selected = types.filter((x) => x.selected);
      if (selected.length === 0) {
        return [];
      }
      return items.filter((item) =>
        selected.some((type) => type.id === item.assemblyItem.typeOfMaterialId)
      );
    })
  );

  groupItems$ = this.filterItems$.pipe(
    map((list) => this.groupByMaterialType(list))
  );

  constructor() {}

  update(): void {
    this.schedulerApiService.operators().subscribe((res) => {
      this.containerService.operatorsOptions = res;
    });
    this.routingService
      .lastRouteData<FilterSchedulerMachineStage>('stage')
      .pipe(take(1))
      .subscribe((stage) => {
        const obsItems: Observable<FilterSchedulerItem[]> =
          stage === 'Machines Section'
            ? this.schedulerApiService.workOrderData()
            : this.schedulerApiService.subWorkOrderData(stage);
        obsItems.subscribe((res) => {
          const itemTypes = this.groupByMaterialType(res, true);
          this.items.next(res);
          this.typeOfItems.next(
            itemTypes.map((item) => ({
              id: item.id,
              title: item.type,
              selected: true,
            }))
          );
        });
        this.schedulerApiService.machineData(stage).subscribe((res) => {
          this.machines.next(res);
        });
      });
    combineLatest([this.items$, this.machines$])
      .pipe(
        filter(([items, machines]) => items.length > 0 && machines.length > 0),
        take(1),
        switchMap(([items, machines]) =>
          this.schedulerApiService
            .currentDayPlanning()
            .pipe(map((dailyPlanning) => ({ dailyPlanning, items, machines })))
        )
      )
      .subscribe({
        next: (res) => {
          // Eliminamos la duplicacion de items en maquinas:
          const itemsOnMachine = res.machines
            .map((machine) =>
              machine.machineItems.map((el) => el.item.id).flat()
            )
            .flat();
          const listOfItems = res.items.filter(
            (item) => !itemsOnMachine.includes(item.id)
          );

          const workOrdersForToday = res.dailyPlanning.filter(
            (item) => !item.planningRecordByDay.finishedAt
          );

          if (workOrdersForToday.length > 0) {
            // Mantener unicamente las work orders que estan en el daily planning
            this.items.next(
              this.setQuantityPlannedToWOWaitingList(
                listOfItems,
                workOrdersForToday
              )
            );
          } else {
            this.items.next(listOfItems);
          }
        },
        complete: () => {
          console.log('did finish');
        },
      });
  }

  private setQuantityPlannedToWOWaitingList(
    waitingList: FilterSchedulerItem[],
    workOrdersPlanned: ResponsePlanningDayQuantity[]
  ): FilterSchedulerItem[] {
    return waitingList.filter((wl) => {
      const workOrderIsPlanned = workOrdersPlanned.find(
        (plan) => plan.workOrder.transactionId === wl.workOrderId
      );

      if (workOrderIsPlanned) {
        wl.quantityPlanned = workOrderIsPlanned.quantity;
        return true;
      }
      return false;
    });
  }

  updateTypeOfItems(option: { id: number; selected: boolean }): void {
    this.typeOfItems.next(
      this.typeOfItems.getValue().map((item) => {
        if (item.id === option.id) {
          return { ...item, selected: option.selected };
        }
        return item;
      })
    );
  }

  private groupByMaterialType(
    list: FilterSchedulerItem[],
    keysOnly = false
  ): { type: string; id: number; items: FilterSchedulerItem[] }[] {
    const mapData = new Map<
      number,
      { type: string; id: number; items: FilterSchedulerItem[] }
    >();

    for (const item of list) {
      const itemType = item.assemblyItem.typeOfMaterial;
      if (!itemType) {
        continue;
      }
      const key = itemType.id;

      if (!mapData.has(key)) {
        mapData.set(key, {
          type: itemType.title,
          id: itemType.id,
          items: [],
        });
      }
      if (!keysOnly) {
        mapData.get(key).items.push(item);
      }
    }

    return Array.from(mapData.values());
  }
}
